import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LLMSettings,
  PromptMode,
  ProviderModel,
  ProviderModelType,
  RerankMode,
  RetrieveMode,
  RoutingSelectorType,
  SearchPipelineOutput,
} from '../../../gamma-api';
import IbButton from '../common/IbButton';
import IbTypography from '../common/IbTypography';
import ModelSelector from '../ModelSelector';
import IbSwitch from '../common/IbSwitch';
import PromptEditorExpandable from '../common/PromptEditorExpandable';
import Switcher from '../common/Switcher';
import { getDefaultIfUndefined } from '../../utils/typeUtil';
import {
  DEFAULT_CONTEXT_PROMPT_TEMPLATE,
  DEFAULT_MULTI_SELECT_PROMPT_TMPL,
  DEFAULT_PARAPHRASE_NUM_QUERIES,
  DEFAULT_PARAPHRASE_QUERY_GENERATION_PROMPT_TEMPLATE,
  DEFAULT_SINGLE_SELECT_PROMPT_TMPL,
  DEFAULT_TOKEN_LIMIT,
  DEFAULT_RETRIEVE_TOP_K,
  DEFAULT_LLM_RERANKER_CHOICE_SELECT_PROMPT_TEMPLATE,
  DEFAULT_RERANK_TOP_K,
  DEFAULT_LLM_RERANKER_CHOICE_BATCH_SIZE,
  MIN_RETRIEVE_TOP_K,
  MIN_RERANK_TOP_K,
  MIN_LLM_RERANKER_CHOICE_BATCH_SIZE,
  MIN_PARAPHRASE_NUM_QUERIES,
  MIN_TOKEN_LIMIT,
} from '../../constants/projectPipeline';
import IbCollapse, { IbCollapsePanel } from '../common/IbCollapse';
import IbScrollSpy from '../common/IbScrollSpy';
import IbNumberInput from '../common/IbNumberInput';
import { toNumberOrUndefined } from '../../utils/numberUtil';
import { PromptLanguage } from '../common/PromptEditor/const';
import { CheckFieldValueFnMap, useValidationForm } from '../../utils/validationUtil';
import { buildModelSelectorOptions, buildModelSelectorValue } from '../ModelSelector/utils';

import {
  BLOCK_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  MAIN_CLASS_NAME,
  CONTENT_CLASS_NAME,
  FOOTER_CLASS_NAME,
  SWITCH_DESCRIPTION_CLASS_NAME,
} from './const';

export enum ProjectSearchPipelineSettingsLayout {
  TESTING_CHAT = 'TESTING_CHAT',
  PROJECT_SETTINGS = 'PROJECT_SETTINGS',
}

type SearchPipelineSettingsForm = {
  llm: LLMSettings | undefined;
};

export interface IProjectSearchPipelineProps {
  layout: ProjectSearchPipelineSettingsLayout;
  searchPipeline?: SearchPipelineOutput;
  providers: ProviderModel[];
  providersLoading: boolean;
  onChange?: (searchPipeline: SearchPipelineOutput) => void;
  onReset?: () => void;
  onSave?: () => Promise<void>;
}

const ProjectSearchPipeline: React.FC<IProjectSearchPipelineProps> = ({
  layout,
  searchPipeline,
  providers,
  providersLoading,
  onChange,
  onReset,
  onSave,
}) => {
  const { t } = useTranslation();

  const checkFieldValueFnMap: CheckFieldValueFnMap<SearchPipelineSettingsForm> = {
    llm: (value: LLMSettings | undefined) => {
      if (!value?.model_name) return false;

      const modelValue = buildModelSelectorValue(value?.provider_id, value?.model_name);
      const options = buildModelSelectorOptions(providers, ProviderModelType.Llm);
      return options.some((o) => o.value === modelValue);
    },
  };

  const initialValues: SearchPipelineSettingsForm = {
    llm: searchPipeline?.llm,
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    validate,
    reset,
    updateFormValues,
    getShowError,
    getErrorMessage,
  } = useValidationForm<SearchPipelineSettingsForm>(initialValues, checkFieldValueFnMap);

  const [saving, setSaving] = useState(false);

  const [retrieveTopKShowDefault, setRetrieveTopKShowDefault] = useState(true);
  const [rerankTopKShowDefault, setRerankTopKShowDefault] = useState(true);
  const [rerankLLMRerankerBatchSizeShowDefault, setRerankLLMRerankerBatchSizeShowDefault] = useState(true);
  const [retrieveParaphraseNumQueriesShowDefault, setRetrieveParaphraseNumQueriesShowDefault] = useState(true);
  const [synthesizeChatHistoryTokenLimitShowDefault, setSynthesizeChatHistoryTokenLimitShowDefault] = useState(true);

  const retrieveTopK = retrieveTopKShowDefault
    ? getDefaultIfUndefined(searchPipeline?.retrieve?.top_k, DEFAULT_RETRIEVE_TOP_K)
    : searchPipeline?.retrieve?.top_k;

  const rerankTopK = rerankTopKShowDefault
    ? getDefaultIfUndefined(searchPipeline?.rerank?.top_k, DEFAULT_RERANK_TOP_K)
    : searchPipeline?.rerank?.top_k;

  const rerankLLMRerankerBatchSize = rerankLLMRerankerBatchSizeShowDefault
    ? getDefaultIfUndefined(searchPipeline?.rerank?.llm_reranker?.batch_size, DEFAULT_LLM_RERANKER_CHOICE_BATCH_SIZE)
    : searchPipeline?.rerank?.llm_reranker?.batch_size;

  const retrieveParaphraseNumQueries = retrieveParaphraseNumQueriesShowDefault
    ? getDefaultIfUndefined(searchPipeline?.retrieve?.paraphrase?.num_queries, DEFAULT_PARAPHRASE_NUM_QUERIES)
    : searchPipeline?.retrieve?.paraphrase?.num_queries;

  const synthesizeChatHistoryTokenLimit = synthesizeChatHistoryTokenLimitShowDefault
    ? getDefaultIfUndefined(searchPipeline?.synthesize?.chat_history?.token_limit, DEFAULT_TOKEN_LIMIT)
    : searchPipeline?.synthesize?.chat_history?.token_limit;

  const isRerankLLMMode = searchPipeline?.rerank?.rerank_mode == RerankMode.Llm;

  const isSimplePromptMode =
    !searchPipeline?.synthesize?.prompt_mode || searchPipeline?.synthesize?.prompt_mode == PromptMode.Simple;
  const isAdvancedPromptMode = searchPipeline?.synthesize?.prompt_mode == PromptMode.Advanced;

  const isRoutingSingleSelectorType =
    !searchPipeline?.routing?.selector?.type ||
    searchPipeline?.routing?.selector?.type == RoutingSelectorType.LlmSingleSelector;
  const isRoutingMultiSelectorType = searchPipeline?.routing?.selector?.type == RoutingSelectorType.LlmMultiSelector;

  useEffect(() => {
    updateFormValues(initialValues);
  }, [searchPipeline]);

  const onResetButtonClick = () => {
    onReset?.();
    setRetrieveTopKShowDefault(true);
    setRerankTopKShowDefault(true);
    setRerankLLMRerankerBatchSizeShowDefault(true);
    setRetrieveParaphraseNumQueriesShowDefault(true);
    setSynthesizeChatHistoryTokenLimitShowDefault(true);
    reset(initialValues);
  };

  const onApplyToProjectButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    await onSave?.();
    setSaving(false);
  };

  const onSaveButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    await onSave?.();
    setSaving(false);
  };

  const onRetrieveTopKChange = (value?: number | string) => {
    onChange?.({
      ...searchPipeline,
      retrieve: {
        ...searchPipeline?.retrieve,
        top_k: toNumberOrUndefined(value),
      },
    } as SearchPipelineOutput);
    setRetrieveTopKShowDefault(false);
  };

  const onRetrieveModeChange = () => {
    onChange?.({
      ...searchPipeline,
      retrieve: {
        ...searchPipeline?.retrieve,
        mode: searchPipeline?.retrieve?.mode == RetrieveMode.Resources ? RetrieveMode.Nodes : RetrieveMode.Resources,
      },
    } as SearchPipelineOutput);
  };

  const onRerankEnabledChange = () => {
    onChange?.({
      ...searchPipeline,
      rerank: {
        ...searchPipeline?.rerank,
        enabled: !searchPipeline?.rerank?.enabled,
        rerank_mode: searchPipeline?.rerank?.rerank_mode || RerankMode.Llm,
      },
    } as SearchPipelineOutput);
  };

  const onRerankTopKChange = (value?: number | string) => {
    onChange?.({
      ...searchPipeline,
      rerank: {
        ...searchPipeline?.rerank,
        top_k: toNumberOrUndefined(value),
      },
    } as SearchPipelineOutput);
    setRerankTopKShowDefault(false);
  };

  const onRerankLLMRerankerLLMChange = (llm: LLMSettings) => {
    onChange?.({
      ...searchPipeline,
      rerank: {
        ...searchPipeline?.rerank,
        llm_reranker: {
          ...searchPipeline?.rerank?.llm_reranker,
          llm,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRerankLLMRerankerPromptChange = (value: string) => {
    onChange?.({
      ...searchPipeline,
      rerank: {
        ...searchPipeline?.rerank,
        llm_reranker: {
          ...searchPipeline?.rerank?.llm_reranker,
          prompt: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRerankLLMRerankerBatchSizeChange = (value?: number | string) => {
    onChange?.({
      ...searchPipeline,
      rerank: {
        ...searchPipeline?.rerank,
        llm_reranker: {
          ...searchPipeline?.rerank?.llm_reranker,
          batch_size: toNumberOrUndefined(value),
        },
      },
    } as SearchPipelineOutput);
    setRerankLLMRerankerBatchSizeShowDefault(false);
  };

  const onLLMChange = (llm: LLMSettings) => {
    onChange?.({ ...searchPipeline, llm } as SearchPipelineOutput);
    onFieldChange('llm', llm);
  };

  const onLLMBlur = () => onFieldBlur('llm');

  const onPromptModeChange = (value: string | boolean) => {
    onChange?.({
      ...searchPipeline,
      synthesize: {
        ...searchPipeline?.synthesize,
        prompt_mode: value,
      },
    } as SearchPipelineOutput);
  };

  const onSynthesizeSimplePromptsPromptChange = (value: string) => {
    onChange?.({
      ...searchPipeline,
      synthesize: {
        ...searchPipeline?.synthesize,
        simple_prompts: {
          ...searchPipeline?.synthesize?.simple_prompts,
          prompt: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onSynthesizeAdvancedPromptsContextPromptChange = (value: string) => {
    onChange?.({
      ...searchPipeline,
      synthesize: {
        ...searchPipeline?.synthesize,
        advanced_prompts: {
          ...searchPipeline?.synthesize?.advanced_prompts,
          context_prompt: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRetrieveParaphraseEnabledChange = () => {
    onChange?.({
      ...searchPipeline,
      retrieve: {
        ...searchPipeline?.retrieve,
        paraphrase: {
          ...searchPipeline?.retrieve?.paraphrase,
          enabled: !searchPipeline?.retrieve?.paraphrase?.enabled,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRetrieveParaphraseNumQueriesChange = (value?: number | string) => {
    onChange?.({
      ...searchPipeline,
      retrieve: {
        ...searchPipeline?.retrieve,
        paraphrase: {
          ...searchPipeline?.retrieve?.paraphrase,
          num_queries: toNumberOrUndefined(value),
        },
      },
    } as SearchPipelineOutput);
    setRetrieveParaphraseNumQueriesShowDefault(false);
  };

  const onRetrieveParaphraseQueryGenerationPromptChange = (value: string) => {
    onChange?.({
      ...searchPipeline,
      retrieve: {
        ...searchPipeline?.retrieve,
        paraphrase: {
          ...searchPipeline?.retrieve?.paraphrase,
          query_generation_prompt: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRetrieveParaphraseLLMChange = (llm: LLMSettings) => {
    onChange?.({
      ...searchPipeline,
      retrieve: {
        ...searchPipeline?.retrieve,
        paraphrase: {
          ...searchPipeline?.retrieve?.paraphrase,
          llm,
        },
      },
    } as SearchPipelineOutput);
  };

  const onSynthesizeChatHistoryEnabledChange = () => {
    onChange?.({
      ...searchPipeline,
      synthesize: {
        ...searchPipeline?.synthesize,
        chat_history: {
          ...searchPipeline?.synthesize?.chat_history,
          enabled: !searchPipeline?.synthesize?.chat_history?.enabled,
        },
      },
    } as SearchPipelineOutput);
  };

  const onSynthesizeChatHistoryTokenLimitChange = (value?: number | string) => {
    onChange?.({
      ...searchPipeline,
      synthesize: {
        ...searchPipeline?.synthesize,
        chat_history: {
          ...searchPipeline?.synthesize?.chat_history,
          token_limit: toNumberOrUndefined(value),
        },
      },
    } as SearchPipelineOutput);
    setSynthesizeChatHistoryTokenLimitShowDefault(false);
  };

  const onRoutingEnabledChange = () => {
    onChange?.({
      ...searchPipeline,
      routing: {
        ...searchPipeline?.routing,
        enabled: !searchPipeline?.routing?.enabled,
      },
    } as SearchPipelineOutput);
  };

  const onRoutingSelectorTypeChange = (value: string | boolean) => {
    onChange?.({
      ...searchPipeline,
      routing: {
        ...searchPipeline?.routing,
        selector: {
          ...searchPipeline?.routing?.selector,
          type: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRoutingSelectorSingleSelectorPromptChange = (value: string) => {
    onChange?.({
      ...searchPipeline,
      routing: {
        ...searchPipeline?.routing,
        selector: {
          ...searchPipeline?.routing?.selector,
          single_selector_prompt: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRoutingSelectorMultiSelectorPromptChange = (value: string) => {
    onChange?.({
      ...searchPipeline,
      routing: {
        ...searchPipeline?.routing,
        selector: {
          ...searchPipeline?.routing?.selector,
          multi_selector_prompt: value,
        },
      },
    } as SearchPipelineOutput);
  };

  const onRoutingSelectorLLMChange = (llm: LLMSettings) => {
    onChange?.({
      ...searchPipeline,
      routing: {
        ...searchPipeline?.routing,
        selector: {
          ...searchPipeline?.routing?.selector,
          llm,
        },
      },
    } as SearchPipelineOutput);
  };

  const renderRetrieveTopKBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Retrieval: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Settings: Retrieval: Search selection size')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Settings: Retrieval: Search selection size: Description')}
        </IbTypography.Paragraph>
        <IbNumberInput min={MIN_RETRIEVE_TOP_K} value={retrieveTopK ?? undefined} onChange={onRetrieveTopKChange} />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch
            checked={searchPipeline?.retrieve?.mode === RetrieveMode.Resources}
            size="small"
            onChange={onRetrieveModeChange}
          />
          <IbTypography.Paragraph strong>{t('Settings: Retrieval: Mode')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
    </div>
  );

  const renderRerankBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Rerank')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={searchPipeline?.rerank?.enabled || false} size="small" onChange={onRerankEnabledChange} />
          <IbTypography.Paragraph strong>{t('Settings: Rerank: Enable rerank')}</IbTypography.Paragraph>
        </IbTypography>
        <IbTypography className={SWITCH_DESCRIPTION_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{t('Settings: Rerank: Description')}</IbTypography.Paragraph>
        </IbTypography>
      </div>

      {searchPipeline?.rerank?.enabled && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Rerank TopK')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{t('Settings: Rerank TopK: Description')}</IbTypography.Paragraph>
            <IbNumberInput min={MIN_RERANK_TOP_K} value={rerankTopK ?? undefined} onChange={onRerankTopKChange} />
          </div>
          {isRerankLLMMode && (
            <>
              <div className={BLOCK_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {t('Settings: Rerank: LLM')}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph type="secondary">
                  {t('Settings: Rerank: LLM: Description')}
                </IbTypography.Paragraph>
                <ModelSelector.LLM
                  loading={providersLoading}
                  providers={providers}
                  settings={searchPipeline?.rerank.llm_reranker?.llm}
                  onChange={onRerankLLMRerankerLLMChange}
                />
              </div>
              <div className={BLOCK_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {t('Settings: Rerank prompt')}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph type="secondary">
                  {t('Settings: Rerank prompt: Description')}
                </IbTypography.Paragraph>
                <PromptEditorExpandable
                  promptLanguage={PromptLanguage.RERANK}
                  value={getDefaultIfUndefined(
                    searchPipeline?.rerank?.llm_reranker?.prompt,
                    DEFAULT_LLM_RERANKER_CHOICE_SELECT_PROMPT_TEMPLATE
                  )}
                  onChange={onRerankLLMRerankerPromptChange}
                />
              </div>
              <div className={BLOCK_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {t('Settings: Batch size')}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph type="secondary">
                  {t('Settings: Batch size: Description')}
                </IbTypography.Paragraph>
                <IbNumberInput
                  min={MIN_LLM_RERANKER_CHOICE_BATCH_SIZE}
                  value={rerankLLMRerankerBatchSize ?? undefined}
                  onChange={onRerankLLMRerankerBatchSizeChange}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  const renderLLMForResponseGenerationBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Response generation')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Settings: Response generation: LLM model')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Settings: Response generation: LLM model: Description')}
        </IbTypography.Paragraph>
        <ModelSelector.LLM
          descriptor={
            getShowError(validationState.llm) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.llm, t('Settings: Response generation: LLM model: Error'))}
              </IbTypography.Paragraph>
            )
          }
          loading={providersLoading}
          providers={providers}
          settings={searchPipeline?.llm}
          status={getShowError(validationState.llm) ? 'error' : 'default'}
          onBlur={onLLMBlur}
          onChange={onLLMChange}
        />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Settings: Answer prompt')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">{t('Settings: Answer prompt: Description')}</IbTypography.Paragraph>
        <Switcher
          options={[
            { value: PromptMode.Simple, label: t('Settings: Simple') },
            { value: PromptMode.Advanced, label: t('Settings: Advanced') },
          ]}
          value={searchPipeline?.synthesize?.prompt_mode}
          onChange={onPromptModeChange}
        />
        {isSimplePromptMode && (
          <PromptEditorExpandable
            promptLanguage={PromptLanguage.RESPONSE_GENERATION}
            value={searchPipeline?.synthesize?.simple_prompts?.prompt}
            onChange={onSynthesizeSimplePromptsPromptChange}
          />
        )}
        {isAdvancedPromptMode && (
          <PromptEditorExpandable
            promptLanguage={PromptLanguage.RESPONSE_GENERATION}
            value={getDefaultIfUndefined(
              searchPipeline?.synthesize?.advanced_prompts?.context_prompt,
              DEFAULT_CONTEXT_PROMPT_TEMPLATE
            )}
            onChange={onSynthesizeAdvancedPromptsContextPromptChange}
          />
        )}
      </div>
    </div>
  );

  const renderRetrieveParaphraseBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Paraphrasing')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch
            checked={searchPipeline?.retrieve?.paraphrase?.enabled || false}
            size="small"
            onChange={onRetrieveParaphraseEnabledChange}
          />
          <IbTypography.Paragraph strong>{t('Settings: Paraphrasing: Enable paraphrasing')}</IbTypography.Paragraph>
        </IbTypography>
        <IbTypography className={SWITCH_DESCRIPTION_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">
            {t('Settings: Paraphrasing: Enable paraphrasing: Description')}
          </IbTypography.Paragraph>
        </IbTypography>
      </div>
      {searchPipeline?.retrieve?.paraphrase?.enabled && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Maximum number of queries generated')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Maximum number of queries generated: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_PARAPHRASE_NUM_QUERIES}
              value={retrieveParaphraseNumQueries ?? undefined}
              onChange={onRetrieveParaphraseNumQueriesChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbCollapse>
              <IbCollapsePanel
                key="paraphrase-configure-prompt"
                header={t('Settings: Configure prompt for generating queries')}
              >
                <div className={BLOCK_ITEM_CLASS_NAME}>
                  <IbTypography.Paragraph strong type="secondary">
                    {t('Settings: Prompt for generating queries')}
                  </IbTypography.Paragraph>
                  <IbTypography.Paragraph type="secondary">
                    {t('Settings: Prompt for generating queries: Description')}
                  </IbTypography.Paragraph>
                  <PromptEditorExpandable
                    promptLanguage={PromptLanguage.PARAPHRASING}
                    value={getDefaultIfUndefined(
                      searchPipeline?.retrieve?.paraphrase?.query_generation_prompt,
                      DEFAULT_PARAPHRASE_QUERY_GENERATION_PROMPT_TEMPLATE
                    )}
                    onChange={onRetrieveParaphraseQueryGenerationPromptChange}
                  />
                </div>
                <div className={BLOCK_ITEM_CLASS_NAME}>
                  <IbTypography.Paragraph strong type="secondary">
                    {t('Settings: Paraphrasing: LLM model')}
                  </IbTypography.Paragraph>
                  <IbTypography.Paragraph type="secondary">
                    {t('Settings: Paraphrasing: LLM model: Description')}
                  </IbTypography.Paragraph>
                  <ModelSelector.LLM
                    loading={providersLoading}
                    providers={providers}
                    settings={searchPipeline?.retrieve?.paraphrase?.llm}
                    onChange={onRetrieveParaphraseLLMChange}
                  />
                </div>
              </IbCollapsePanel>
            </IbCollapse>
          </div>
        </>
      )}
    </div>
  );

  const renderChatHistoryBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Chat history')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch
            checked={searchPipeline?.synthesize?.chat_history?.enabled || false}
            size="small"
            onChange={onSynthesizeChatHistoryEnabledChange}
          />
          <IbTypography.Paragraph strong>
            {t('Settings: Chat history: Enable chat history processing')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbTypography className={SWITCH_DESCRIPTION_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">
            {t('Settings: Chat history: Enable chat history processing: Description')}
          </IbTypography.Paragraph>
        </IbTypography>
      </div>
      {searchPipeline?.synthesize?.chat_history?.enabled && (
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Settings: Chat history: Maximum history size')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">
            {t('Settings: Chat history: Maximum history size: Description')}
          </IbTypography.Paragraph>
          <IbNumberInput
            min={MIN_TOKEN_LIMIT}
            value={synthesizeChatHistoryTokenLimit || undefined}
            onChange={onSynthesizeChatHistoryTokenLimitChange}
          />
        </div>
      )}
    </div>
  );

  // NOTE: not implemented yet
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderRoutingBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Query routing')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch
            checked={searchPipeline?.routing?.enabled || false}
            size="small"
            onChange={onRoutingEnabledChange}
          />
          <IbTypography.Paragraph strong>{t('Settings: Query routing: Enable routing')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
      {searchPipeline?.routing?.enabled && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <Switcher
              options={[
                { value: RoutingSelectorType.LlmSingleSelector, label: t('Settings: Single') },
                { value: RoutingSelectorType.LlmMultiSelector, label: t('Settings: Multiple') },
              ]}
              value={searchPipeline?.routing?.selector?.type}
              onChange={onRoutingSelectorTypeChange}
            />
          </div>
          {isRoutingSingleSelectorType && (
            <div className={BLOCK_ITEM_CLASS_NAME}>
              <IbCollapse>
                <IbCollapsePanel
                  key="routing-single-selector"
                  header={t('Settings: Configure prompt for generating queries')}
                >
                  <IbTypography.Paragraph strong type="secondary">
                    {t('Settings: Route selection prompt')}
                  </IbTypography.Paragraph>
                  <IbTypography.Paragraph type="secondary">
                    {t('Settings: Description for input')}
                  </IbTypography.Paragraph>
                  <PromptEditorExpandable
                    promptLanguage={PromptLanguage.ROUTING}
                    value={getDefaultIfUndefined(
                      searchPipeline?.routing?.selector?.single_selector_prompt,
                      DEFAULT_SINGLE_SELECT_PROMPT_TMPL
                    )}
                    onChange={onRoutingSelectorSingleSelectorPromptChange}
                  />
                </IbCollapsePanel>
              </IbCollapse>
            </div>
          )}
          {isRoutingMultiSelectorType && (
            <div className={BLOCK_ITEM_CLASS_NAME}>
              <IbCollapse>
                <IbCollapsePanel
                  key="routing-multi-selector"
                  header={t('Settings: Configure prompt for generating queries')}
                >
                  <IbTypography.Paragraph strong type="secondary">
                    {t('Settings: Route selection prompt')}
                  </IbTypography.Paragraph>
                  <IbTypography.Paragraph type="secondary">
                    {t('Settings: Description for input')}
                  </IbTypography.Paragraph>
                  <PromptEditorExpandable
                    promptLanguage={PromptLanguage.ROUTING}
                    value={getDefaultIfUndefined(
                      searchPipeline?.routing?.selector?.multi_selector_prompt,
                      DEFAULT_MULTI_SELECT_PROMPT_TMPL
                    )}
                    onChange={onRoutingSelectorMultiSelectorPromptChange}
                  />
                </IbCollapsePanel>
              </IbCollapse>
            </div>
          )}
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: LLM model')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{t('Settings: Description for input')}</IbTypography.Paragraph>
            <ModelSelector.LLM
              loading={providersLoading}
              providers={providers}
              settings={searchPipeline?.routing.selector?.llm}
              onChange={onRoutingSelectorLLMChange}
            />
          </div>
        </>
      )}
    </div>
  );

  const sections = [
    { id: 'retrieval', title: t('Settings: Retrieval: Title'), content: renderRetrieveTopKBlock() },
    {
      id: 'response-generation',
      title: t('Settings: Response generation'),
      content: renderLLMForResponseGenerationBlock(),
    },
    { id: 'paraphrasing', title: t('Settings: Paraphrasing'), content: renderRetrieveParaphraseBlock() },
    { id: 'chat-history', title: t('Settings: Chat history'), content: renderChatHistoryBlock() },
    { id: 'rerank', title: t('Settings: Rerank'), content: renderRerankBlock() },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy displayNav={layout == ProjectSearchPipelineSettingsLayout.PROJECT_SETTINGS} sections={sections} />
      </div>
      {layout == ProjectSearchPipelineSettingsLayout.TESTING_CHAT && (
        <div className={FOOTER_CLASS_NAME}>
          <IbButton disabled={saving} type="secondary" onClick={onApplyToProjectButtonClick}>
            {t('Settings: Apply to project')}
          </IbButton>
          <IbButton status="error" type="secondary" onClick={onResetButtonClick}>
            {t('Settings: Reset')}
          </IbButton>
        </div>
      )}
      {layout == ProjectSearchPipelineSettingsLayout.PROJECT_SETTINGS && (
        <div className={FOOTER_CLASS_NAME}>
          <IbButton disabled={saving} onClick={onSaveButtonClick}>
            {t('Settings: Save')}
          </IbButton>
        </div>
      )}
    </div>
  );
};

export default ProjectSearchPipeline;
